"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/components/ui/use-toast";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import {
  useGenerateGnreMutation,
  useGetEligibleGnreInvoicesQuery,
} from "@/services/taxt-integration";
import { AlertTriangle, Calendar, Download, ShieldCheck } from "lucide-react";
import { useFilterState } from "../../../filter/hooks/filterState";
import { generateGnreSchema, GenerateGnreFormData } from "./validate-form";
import { SuccessMessage } from "../shared";

interface GeneratedItem {
  invoice_number: number;
  batch: string;
}

/**
 * Janela padrão = mês anterior (1º ao último dia), espelhando o default do
 * backend em findAllInvoices, para que as notas elegíveis carreguem assim que
 * o modal abre, mesmo sem o usuário ter escolhido datas na tabela.
 */
function getDefaultGnreWindow(): { from: string; to: string } {
  const pad = (n: number) => String(n).padStart(2, "0");
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  const today = new Date();
  const lastMonth = new Date(
    today.getFullYear(),
    today.getMonth() - 1,
    today.getDate(),
  );
  const from = new Date(lastMonth.getFullYear(), lastMonth.getMonth(), 1);
  const to = new Date(today.getFullYear(), today.getMonth(), 0);

  return { from: fmt(from), to: fmt(to) };
}

export function GenerateGNREModal() {
  const filterState = useFilterState();
  const { handleModal } = useDialogModal();
  const [pendingSubmit, setPendingSubmit] =
    useState<GenerateGnreFormData | null>(null);
  const [confirmStep, setConfirmStep] = useState<
    "confirm" | "duplicate-warning" | null
  >(null);

  const [generateGnre, { isLoading: isGenerating }] = useGenerateGnreMutation();

  const defaultWindow = getDefaultGnreWindow();

  const form = useForm<GenerateGnreFormData>({
    resolver: zodResolver(generateGnreSchema),
    defaultValues: {
      fromDate: filterState?.filters?.from_date || defaultWindow.from,
      toDate: filterState?.filters?.to_date || defaultWindow.to,
      paymentDate: "",
    },
  });

  const { watch } = form;
  const fromDate = watch("fromDate");
  const toDate = watch("toDate");

  const [isSuccess, setIsSuccess] = useState(false);

  const [dontRegenerateProcessed, setDontRegenerateProcessed] = useState(true);

  // Notas elegíveis do período (mesma regra da geração). O backend já separa
  // o que processar do que já foi processado, respeitando filterGenerated.
  const {
    data: eligibleData,
    isFetching: isLoadingInvoices,
    isError: isEligibleError,
  } = useGetEligibleGnreInvoicesQuery(
    {
      startDate: fromDate,
      endDate: toDate,
      filterGenerated: dontRegenerateProcessed,
    },
    { skip: !fromDate || !toDate },
  );

  const displayedInvoices: GeneratedItem[] = eligibleData?.toProcess ?? [];
  const alreadyProcessedInvoices: GeneratedItem[] =
    eligibleData?.alreadyProcessed ?? [];

  const executeGeneration = async (
    data: GenerateGnreFormData,
    filterGenerated: boolean,
  ) => {
    try {
      await generateGnre({
        params: {
          startDate: data.fromDate,
          endDate: data.toDate,
          filterGenerated,
        },
        body: { paymentDate: data.paymentDate },
      }).unwrap();

      setIsSuccess(true);
      toast({
        variant: "success",
        description: "GNRE solicitada com sucesso!",
        duration: 5000,
      });
    } catch (err: unknown) {
      const error = err as { data?: { messages?: string[] } };
      console.error("Erro ao gerar GNRE:", err);
      toast({
        variant: "destructive",
        description: error?.data?.messages?.[0] || "Erro ao gerar GNRE",
        duration: 5000,
      });
    }
  };

  const onSubmit = async (data: GenerateGnreFormData) => {
    setPendingSubmit(data);
    setConfirmStep("confirm");
  };

  const handleCancel = () => {
    form.reset();
    handleModal({ isOpen: false });
  };

  if (isSuccess) {
    return <SuccessMessage />;
  }

  if (pendingSubmit && confirmStep === "confirm") {
    return (
      <div className="flex flex-col gap-3 py-4">
        <p className="text-sm text-muted-foreground">
          Tem certeza que deseja processar as GNREs para o período selecionado?
        </p>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setPendingSubmit(null);
              setConfirmStep(null);
            }}
            className="flex-1 h-9 bg-transparent"
          >
            Voltar
          </Button>
          <Button
            type="button"
            className="flex-1 h-9"
            onClick={() => {
              if (!dontRegenerateProcessed) {
                setConfirmStep("duplicate-warning");
              } else {
                executeGeneration(pendingSubmit, true);
              }
            }}
          >
            Sim, processar
          </Button>
        </div>
      </div>
    );
  }

  if (pendingSubmit && confirmStep === "duplicate-warning") {
    return (
      <div className="flex flex-col gap-3 py-4">
        <p className="text-sm font-medium text-amber-800">
          Existem guias já geradas para este período. Continuar poderá causar
          duplicidade. Deseja prosseguir mesmo assim?
        </p>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setPendingSubmit(null);
              setConfirmStep(null);
            }}
            className="flex-1 h-9 bg-transparent"
          >
            Cancelar
          </Button>
          <Button
            type="button"
            className="flex-1 h-9"
            disabled={isGenerating}
            onClick={() => executeGeneration(pendingSubmit, false)}
          >
            {isGenerating ? "Processando..." : "Sim, regerar"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="bg-background w-full flex flex-col overflow-hidden"
      >
        <div className="space-y-6 py-4 h-auto overflow-auto">
          <div className="space-y-4 -mt-2">
            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="fromDate"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <Label
                      htmlFor="from_date"
                      className="text-sm font-medium text-muted-foreground"
                    >
                      Data Inicial
                    </Label>
                    <FormControl>
                      <Input
                        id="from_date"
                        type="date"
                        className="w-full h-9 text-sm"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="toDate"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <Label
                      htmlFor="to_date"
                      className="text-sm font-medium text-muted-foreground"
                    >
                      Data Final
                    </Label>
                    <FormControl>
                      <Input
                        id="to_date"
                        type="date"
                        className="h-9 text-sm"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="paymentDate"
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <Label
                    htmlFor="payment_date"
                    className="text-sm font-medium text-muted-foreground"
                  >
                    Data de Pagamento
                  </Label>
                  <FormControl>
                    <div className="relative">
                      <Input
                        id="payment_date"
                        type="date"
                        className="pl-9 h-9 text-sm"
                        {...field}
                      />
                      <Calendar className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <Card className="bg-[#f6f7f9] p-2 px-4 flex flex-row gap-1 justify-between border border-black/20">
            <div className="font-medium text-base text-gray-700">
              Não regerar guias processadas
              <div className="text-sm text-gray-500 max-w-sm">
                Se ativado, guias já processadas não serão geradas novamente.
              </div>
            </div>
            <div className="flex items-center justify-end">
              <Switch
                checked={dontRegenerateProcessed}
                onCheckedChange={setDontRegenerateProcessed}
                id="dont-regenerate-switch"
              />
            </div>
          </Card>

          {alreadyProcessedInvoices.length > 0 && (
            <Card className="border border-amber-300 bg-amber-50 shadow-sm p-4">
              <div className="flex items-center gap-2 font-medium text-sm text-amber-800 mb-2">
                <AlertTriangle className="h-4 w-4" />
                Guias já processadas no período (
                {alreadyProcessedInvoices.length})
              </div>
              <ScrollArea className="h-auto max-h-20 overflow-auto w-full rounded-md border border-amber-200 bg-amber-50/50 p-2">
                <div className="space-y-1.5">
                  {alreadyProcessedInvoices.map((invoice, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center text-xs text-amber-900"
                    >
                      <span className="font-medium">
                        NF: {invoice.invoice_number}
                      </span>
                      <span className="text-amber-700">
                        Lote: {invoice.batch}
                      </span>
                    </div>
                  ))}
                </div>
              </ScrollArea>
              {dontRegenerateProcessed && (
                <p className="text-xs text-amber-700 mt-2">
                  Estas notas serão ignoradas pois o bloqueio de regeração está
                  ativo.
                </p>
              )}
              {!dontRegenerateProcessed && (
                <p className="text-xs text-red-600 font-medium mt-2">
                  Atenção: estas notas serão regeradas. Será solicitada
                  confirmação ao processar.
                </p>
              )}
            </Card>
          )}

          <Card className="border border-gray-200 bg-white shadow-sm p-4 mb-3">
            <div className="flex items-center gap-2 font-medium text-sm text-gray-700 mb-2">
              <ShieldCheck className="h-4 w-4 text-emerald-600" />
              Notas a processar ({displayedInvoices.length})
            </div>
            <ScrollArea className="h-auto max-h-16 overflow-auto w-full rounded-md border bg-background/50 p-2">
              {isLoadingInvoices ? (
                <div className="flex items-center justify-center py-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary/20 border-t-primary" />
                </div>
              ) : isEligibleError ? (
                <div className="text-xs text-red-600 text-center py-2">
                  Erro ao carregar as notas elegíveis. Verifique o período e
                  tente novamente.
                </div>
              ) : displayedInvoices.length === 0 ? (
                <div className="text-xs text-muted-foreground text-center py-2">
                  Nenhuma nota encontrada para o período selecionado.
                </div>
              ) : (
                <div className="space-y-1.5">
                  {displayedInvoices.map((invoice, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center text-xs"
                    >
                      <span className="font-medium">
                        NF: {invoice.invoice_number}
                      </span>
                      <span className="text-muted-foreground">
                        Lote: {invoice.batch}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </Card>
        </div>

        <div className="flex gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            className="flex-1 h-10 bg-transparent"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            className="flex-1 h-10 flex gap-2"
            disabled={
              !form.formState.isValid ||
              isGenerating ||
              isLoadingInvoices ||
              isEligibleError ||
              displayedInvoices.length === 0
            }
          >
            <Download className="h-4 w-4" />
            {isGenerating ? "Processando..." : "Processar GNRE"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
